// src/components/ApplyModal.tsx
//
// Quick "Track this application" modal used by the Track button on the Jobs
// (recommendations) page and Advanced Search results. It lets the user confirm
// which resume (and optional cover letter) they used — pre-selected to their
// primary resume — and records a COMPLETE application via POST /auth/jobs/apply.
//
// This does not open the employer site; "View Job" remains the separate action.
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { axiosInstance } from "../utils/axiosConfig";
import { trackEvent } from "../utils/trackEvent";

export interface ApplyJob {
  id: string;
  title: string;
  company: string;
  location?: string;
  url?: string;
  description?: string;
}

interface Resume {
  id: string;
  fileName: string;
  isPrimary: boolean;
}

interface CoverLetter {
  id: string;
  title: string;
}

interface ApplyModalProps {
  job: ApplyJob;
  onClose: () => void;
  // Called once the application is recorded (or already existed) so the parent
  // can mark the job as applied.
  onApplied: (jobId: string) => void;
}

const ApplyModal: React.FC<ApplyModalProps> = ({ job, onClose, onApplied }) => {
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [coverLetters, setCoverLetters] = useState<CoverLetter[]>([]);
  const [resumeUsedId, setResumeUsedId] = useState<string>("");
  const [coverLetterUsedId, setCoverLetterUsedId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [resumeRes, coverRes] = await Promise.all([
          axiosInstance.get("/auth/resumes"),
          axiosInstance.get("/auth/cover-letters").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const resumeList: Resume[] = resumeRes.data?.resumes || [];
        setResumes(resumeList);
        setCoverLetters(coverRes.data || []);
        // Pre-select the primary resume (fall back to the first one).
        const primary = resumeList.find((r) => r.isPrimary) || resumeList[0];
        if (primary) setResumeUsedId(primary.id);
      } catch (err) {
        if (!cancelled) toast.error("Failed to load your resumes");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await axiosInstance.post("/auth/jobs/apply", {
        adzunaJobId: job.id,
        jobTitle: job.title,
        company: job.company,
        location: job.location,
        url: job.url,
        description: job.description,
        resumeUsedId: resumeUsedId || undefined,
        coverLetterUsedId: coverLetterUsedId || undefined,
      });
      trackEvent("application_marked", { company: job.company, source: "apply_modal" });
      toast.success("Application recorded! Track it in the Applications tab.");
      onApplied(job.id);
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error;
      // Already applied → not an error for the user; treat as recorded (no dup).
      if (err?.response?.status === 400 && /already applied/i.test(msg || "")) {
        toast("You've already tracked this job.", { icon: "ℹ️" });
        onApplied(job.id);
        onClose();
        return;
      }
      toast.error(msg || "Failed to record application");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "10px",
          padding: "24px",
          width: "100%",
          maxWidth: "460px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: "18px" }}>Track application</h2>
        <p style={{ margin: "0 0 16px", color: "#555", fontSize: "14px" }}>
          {job.title} — {job.company}
        </p>

        {loading ? (
          <p style={{ color: "#777" }}>Loading your resumes…</p>
        ) : (
          <>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
              Resume used
            </label>
            {resumes.length === 0 ? (
              <p style={{ color: "#c0392b", fontSize: "13px", margin: "0 0 12px" }}>
                No resumes found — you can add one in the Resumes tab. The application will still be tracked.
              </p>
            ) : (
              <select
                value={resumeUsedId}
                onChange={(e) => setResumeUsedId(e.target.value)}
                style={{ width: "100%", padding: "10px", marginBottom: "14px", borderRadius: "6px", border: "1px solid #ccc" }}
              >
                {resumes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.fileName}
                    {r.isPrimary ? " (primary)" : ""}
                  </option>
                ))}
              </select>
            )}

            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
              Cover letter (optional)
            </label>
            <select
              value={coverLetterUsedId}
              onChange={(e) => setCoverLetterUsedId(e.target.value)}
              style={{ width: "100%", padding: "10px", marginBottom: "20px", borderRadius: "6px", border: "1px solid #ccc" }}
            >
              <option value="">None</option>
              {coverLetters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{ padding: "10px 18px", borderRadius: "6px", border: "1px solid #ccc", background: "white", cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || submitting}
            style={{
              padding: "10px 18px",
              borderRadius: "6px",
              border: "none",
              background: loading || submitting ? "#95a5a6" : "#27ae60",
              color: "white",
              fontWeight: 600,
              cursor: loading || submitting ? "default" : "pointer",
            }}
          >
            {submitting ? "Recording…" : "Track application"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyModal;
